import { isVideoUrl } from '@/lib/posts/is-video-url';

export interface ChildRenditionState {
  aspectRatio: string;
  focalX: number;
  focalY: number;
  zoom: number;
  variantUrl?: string | null;
  sourceFingerprint?: string;
  transformVersion?: string;
}

export interface ChildMediaState {
  childId: string;
  url: string;
  kind: 'image' | 'video';
  sourceMediaId?: string | null;
  sourceFingerprint: string;
  renditions: Record<string, ChildRenditionState>;
  /** Author-written description of an image, sent as alt text. Dropped when the image is replaced. */
  altText?: string;
}

function fingerprint(url: string): string {
  let hash = 2166136261;
  for (let index = 0; index < url.length; index += 1) {
    hash ^= url.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `fnv1a-${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

function newChildId(url: string): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `child-${fingerprint(`${url}:${Date.now()}:${Math.random()}`)}`;
}

function createChild(url: string, childId = newChildId(url)): ChildMediaState {
  return {
    childId,
    url,
    kind: isVideoUrl(url) ? 'video' : 'image',
    sourceFingerprint: fingerprint(url),
    renditions: {},
  };
}

/**
 * Reconciles a URL-only mutation against stable child state. Matching uses a
 * queue per URL, so duplicate assets retain distinct child identities. A
 * changed URL preserves the slot child ID but drops source-bound renditions.
 */
export function reconcileChildMediaUrls(
  current: ChildMediaState[],
  nextUrls: string[],
): ChildMediaState[] {
  const available = new Map<string, ChildMediaState[]>();
  for (const item of current) {
    const bucket = available.get(item.url) ?? [];
    bucket.push(item);
    available.set(item.url, bucket);
  }
  return nextUrls.map((url, index) => {
    const bucket = available.get(url);
    const matched = bucket?.shift();
    if (matched) return { ...matched, kind: isVideoUrl(url) ? 'video' : 'image' };
    const slotChild = current[index];
    if (slotChild) return createChild(url, slotChild.childId);
    return createChild(url);
  });
}

export function updateChildRendition(
  children: ChildMediaState[],
  childId: string,
  destination: string,
  patch: ChildRenditionState,
): ChildMediaState[] {
  return children.map((child) =>
    child.childId === childId
      ? {
          ...child,
          renditions: {
            ...child.renditions,
            [destination]: {
              ...patch,
              sourceFingerprint: child.sourceFingerprint,
              transformVersion: patch.transformVersion ?? 'image-crop-v1',
              variantUrl: null,
            },
          },
        }
      : child,
  );
}

/** A crop and/or framing change for one destination. */
export interface ChildCommand {
  aspectRatio?: string;
  focalX?: number;
  focalY?: number;
  zoom?: number;
}

/**
 * Apply one authoring command to a destination (`platform:placement`).
 *
 * The scope rule is the whole point, and it comes from the provider rather than
 * from us. Instagram's content-publishing documentation states that "Carousel
 * images are all cropped based on the first image in the carousel", so:
 *
 *   - **Aspect ratio is post-level.** It broadcasts to every child. A per-child
 *     ratio is not merely unsupported, it is inert — the provider overrides it,
 *     and any slide baked to a different shape is then cropped a second time.
 *   - **Framing is per-child.** Focal point and zoom decide what is visible
 *     inside that one shared shape, which is the per-slide control authors want.
 *   - **A framing command never invents a ratio.** With no rendition for this
 *     destination there is no frame to move within, so it is a no-op. Seeding
 *     one used to fabricate `1:1` on platforms the author never configured.
 *
 * `null` clears the destination on every child: "Original" is a statement about
 * the post, not about one slide.
 */
export function applyChildCommand(
  children: ChildMediaState[],
  selectedChildId: string,
  destination: string,
  command: ChildCommand | null,
): ChildMediaState[] {
  if (!command) {
    return children.map((item) => {
      const renditions = { ...item.renditions };
      delete renditions[destination];
      return { ...item, renditions };
    });
  }

  if (command.aspectRatio) {
    const ratio = command.aspectRatio;
    // One pass. Routing each child through updateChildRendition would re-map
    // the whole array per child — O(n²) rebuilds for a single broadcast.
    return children.map((item) => {
      // Never stamp a ratio on a video child. Mora has no transcoder, so an
      // explicit crop on a video fails the post closed at publish
      // (videoCropRejection). Broadcasting indiscriminately would turn a mixed
      // image+video carousel unpublishable from one press of "Crop to 4:5" —
      // the quality-fix button is not gated on media kind
      // (PostBody.handleApplyQualityFix) and checkAspect grades media_urls[0],
      // so an image-first carousel offers the fix that would break it. The
      // picker itself is already hidden for video posts.
      if (item.kind === 'video') return item;
      const existing = item.renditions[destination];
      // Focal point and zoom mean nothing except relative to a frame. When the
      // frame changes they are stale for EVERY child, so reset them all rather
      // than recentre the selected slide and leave its siblings positioned
      // against a shape that no longer exists. Re-applying the same ratio (a
      // broadcast triggered by some other edit) preserves framing.
      const changingShape = existing?.aspectRatio !== ratio;
      return {
        ...item,
        renditions: {
          ...item.renditions,
          [destination]: {
            ...existing,
            ...(changingShape ? { focalX: 0.5, focalY: 0.5, zoom: 1 } : {}),
            ...(item.childId === selectedChildId ? command : {}),
            aspectRatio: ratio,
            sourceFingerprint: item.sourceFingerprint,
            transformVersion: existing?.transformVersion ?? 'image-crop-v1',
            // Any cached bake describes the OLD frame; publishing it would ship
            // the previous crop. Mirrors updateChildRendition.
            variantUrl: null,
          },
        },
      };
    });
  }

  const selected = children.find((item) => item.childId === selectedChildId);
  const existing = selected?.renditions[destination];
  if (!existing) return children;
  return updateChildRendition(children, selectedChildId, destination, {
    ...existing,
    ...command,
  });
}

export function readChildMediaState(urls: string[], value: unknown): ChildMediaState[] {
  const raw =
    value &&
    typeof value === 'object' &&
    Array.isArray((value as { childManifest?: unknown }).childManifest)
      ? (value as { childManifest: unknown[] }).childManifest
      : [];
  const parsed = raw.filter(
    (item): item is ChildMediaState =>
      !!item &&
      typeof item === 'object' &&
      typeof (item as ChildMediaState).childId === 'string' &&
      typeof (item as ChildMediaState).url === 'string' &&
      typeof (item as ChildMediaState).sourceFingerprint === 'string' &&
      typeof (item as ChildMediaState).renditions === 'object',
  );
  return reconcileChildMediaUrls(parsed, urls);
}
